// shim requestIdleCallback for Safari
import '~/src/Lib/polyfills'
import i18n from 'i18n-literally'
import { assocPath } from 'ramda'

import { track } from '@minimal-analytics/ga4'
import * as sentry from '@sentry/capacitor'
import { browserTracingIntegration, init as initReactSentry, replayIntegration } from '@sentry/react'

import config from '~/src/App/config'
import {
  clearLegacyTokens,
  getTokens,
  refreshTokens,
  restoreNativeSession,
  shouldRefreshTokensAtStartup,
  shouldRestoreTokensAtStartup,
} from '~/src/Lib/Auth'
import getGlobal from '~/src/Lib/getGlobal'
import { initLogRocket } from '~/src/Lib/LogRocket'
import { importWithRetries } from '~/src/Lib/Utils/lazyWithRetries'
import { isNativePlatform } from '~/src/Native/platform'
import { isSolusOnlyDevOverride } from '~/src/Native/solus/dev'
import { ACCOUNT_CREATION } from '~/src/Register/bundle'
import getStore from '~/src/Store'
import { defaultState as authDefaultState } from '~/src/Store/bundles/auth'
import { getLSM } from '~/src/Store/bundles/me'
import { clearLSM } from '~/src/Store/bundles/me/utils'
import { load } from '~/vendor/redux-localstorage-simple'

import { HARVESTING_FLOW } from './Flow/constants'
import { FACILITY_SEGMENT_PATTERN } from './Routes/constants'
import { isPublicRoute } from './Routes/utils'

let sentryInitialized = false

export function initThirdParty(logger) {
  const global = getGlobal()
  initLogRocket(logger)

  if (config.GA4_TRACKING) {
    logger.debug('starting Google Analytics v4 tracking')
    track(config.GA4_TRACKING)
  }

  if (config.SENTRY_DSN) {
    logger.debug('starting Sentry instrumentation')
    const native = isNativePlatform()
    const options = {
      dsn: config.SENTRY_DSN,
      denyUrls: [/^chrome-extension:/],
      attachThreads: native,
      release: `v${config.VERSION}`,
      environment: config.ENVIRONMENT,
      integrations: [
        browserTracingIntegration({
          shouldCreateSpanForRequest: url =>
            Boolean(url.match(/^https?:\/\/(localhost|[0-9a-z-.]+\.phytochrome\.io|(app|api)\.aroya\.io)/)),
        }),
        replayIntegration({
          blockAllMedia: false,
          maskAllInputs: false,
          maskAllText: false,
          networkDetailAllowUrls: ['https://api.aroya.io', global.location.origin],
          networkRequestHeaders: ['X-App', 'X-Client', 'X-Client-Version', 'X-Facility', 'If-Unmodified-Since'],
          networkResponseHeaders: ['Allow'],
        }),
      ],
      replaysSessionSampleRate: 1,
      sendDefaultPii: true,
      tracesSampleRate: 0.1,
      tracePropagationTargets: [getGlobal().location.origin, config.API_URL],
    }
    if (native) {
      sentry.init(options, initReactSentry)
      sentry.setTag('app_variant', 'aroya-next')
    } else {
      initReactSentry(options)
    }
    sentry.setTag('app', config.APP)
    sentryInitialized = true
  }
}

export async function prepareMockServiceWorker(apiMocked, logger) {
  if (apiMocked) {
    const { default: handlers } = await import('~/test/mocks/handlers')
    const { setupWorker, rest } = await import('msw')
    const global = getGlobal()
    const worker = setupWorker(...handlers)
    global.msw = {
      worker,
      rest,
    }
    await worker.start()
    let resolved = false
    let resolve
    let timeout
    let interval

    worker.events.on('request:start', req => {
      if (!resolved) {
        logger.debug('marking msw ready')
        resolve(worker)
        clearTimeout(timeout)
      }
      const headers = req.headers.all()
      const client = headers['x-client']
      if (!client) return
      const appURL = global.location.href
      global.msw.appURL = appURL
      global.msw.clientID = client
    })

    await new Promise(r => {
      resolve = r
      timeout = setTimeout(() => {
        logger.debug('timed out waiting for msw')
        resolved = true
        r()
        clearInterval(interval)
      }, 3000)
      interval = setInterval(async () => {
        try {
          // deepcode ignore PromiseNotCaughtGeneral: We are catching exceptions
          await fetch(new URL('/msw', location.href)).then(response => {
            if (response.status === 204) {
              clearInterval(interval)
              clearTimeout(timeout)
              resolved = true
              resolve()
            }
          })
        } catch (err) {
          logger.debug('error while checking if msw is active:', err)
        }
      }, 250)
    })
    if (apiMocked) {
      importWithRetries(() => import('~/test/e2e/utils/idb'))
        .then(pkg => {
          logger.debug('idb:', pkg)
          getGlobal().idb = pkg
        })
        .catch(error => logger.error('error importing idb:', error))
    }
    return worker
  }
  return null
}

const setupUnsavedHarvestingWarning = (store, logger) => {
  const window = getGlobal()
  const setupUnloadHandler = () => {
    logger.debug('[setupUnsavedHarvestingWarning] adding unload handler')
    const unloadHandler = event => {
      logger.debug('[unloadHandler] running')
      const haveUnsaved = store.selectHaveHarvestingUnsavedStates()
      if (haveUnsaved) {
        event.preventDefault()
        logger.debug('[unloadHandler] We have unsaved changes')
        store.doShowUnsavedHarvestingLeaveDialog()
        return 'HAS_UNSAVED'
      }
      return null
    }
    window.addEventListener('beforeunload', unloadHandler)
  }

  window.addEventListener('click', setupUnloadHandler, { capture: true, once: true })
}

export async function initializeAppState(indexedDBData, logger) {
  let user
  let facilities
  let solusOnly = isSolusOnlyDevOverride()
  let currentMembership = null
  let currentFacility = null
  let refreshFailed = false
  let nativeSessionRestoreFailed = false
  if (isNativePlatform()) {
    try {
      await restoreNativeSession()
    } catch (error) {
      nativeSessionRestoreFailed = true
      logger.error('Unable to restore the native session:', error)
      sentry.captureException(error, { tags: { native_login_stage: 'session-restore' } })
    }
  }
  const restoreTokens = shouldRestoreTokensAtStartup()
  if (restoreTokens) {
    try {
      const tokens = shouldRefreshTokensAtStartup() ? await refreshTokens() : getTokens()
      if (!tokens) {
        refreshFailed = true
      } else {
        ;({ user, facilities } = tokens)
        solusOnly =
          isSolusOnlyDevOverride() ||
          (isNativePlatform() && tokens.solusOnly === true && !(Array.isArray(facilities) && facilities.length))
      }
    } catch (error) {
      refreshFailed = true
      logger.error('error refreshing tokens:', error)
    }
  } else {
    clearLegacyTokens()
  }

  if (solusOnly) {
    clearLSM()
  } else {
    ;({ id: currentMembership, facility: currentFacility } = getLSM())
  }

  const { userSettings, mySettings, ...restLoad } = load({
    states: [HARVESTING_FLOW, ACCOUNT_CREATION, 'mySettings'],
    namespace: `__${config.APP}__`,
  })
  const initialData = {
    ...indexedDBData,
    ...restLoad,
    // TODO: get rid of userSettings in May 2025 or when v4.0.0 is released
    mySettings: { ...userSettings, ...mySettings },
    auth: {
      ...authDefaultState,
      authenticated: !!user,
      solusOnly,
      // TODO(aroya-next): Remove this explicit diagnostic once native session restoration is proven in the stores.
      loginFailed: nativeSessionRestoreFailed,
      loginMessage: nativeSessionRestoreFailed
        ? i18n`AROYA could not restore the secure session on this device. Please sign in again.`
        : '',
    },
  }
  if (solusOnly) {
    initialData.currentFacility = {
      current: null,
      next: null,
      nextURL: null,
      ts: 0,
    }
  }
  logger.debug('auth:', initialData.auth)
  const { location } = getGlobal()
  const url = location.href.replace(location.origin, '')

  if (!solusOnly) {
    const match = url.match(FACILITY_SEGMENT_PATTERN)
    if (match && match[1] && match[1] != currentFacility) {
      logger.debug('url is for another facility:', { nextFacility: match[1], currentFacility })
      initialData.currentFacility = {
        current: currentFacility ?? null,
        next: Number(match[1]),
        nextURL: url,
        ts: Date.now(),
      }
    } else if (currentMembership && currentFacility) {
      assocPath(['currentFacility', 'current'], currentFacility, initialData)
    } else if (facilities?.length) {
      let facility = facilities.find(f => f.id == currentFacility)
      if (!facility) {
        ;({ 0: facility } = facilities.slice().sort((a, b) => a.id - b.id))
      }
      assocPath(['currentFacility', 'nextFacility'], facility.id, initialData)
    }
  }

  const reduxStore = await getStore(initialData)

  if (solusOnly) reduxStore.doMarkMeAsOutdated()

  if ((!restoreTokens || refreshFailed) && !isPublicRoute(location.pathname)) {
    logger.warn('Token refresh failed. Redirecting to login page.', {
      currentFacility,
      currentMembership,
    })
    if (sentryInitialized) {
      sentry.captureEvent({
        message: 'Token refresh failed. Redirecting to login page.',
        extra: {
          currentFacility,
          currentMembership,
          reason: 'token-refresh-failed',
        },
      })
    }
    location.href = new URL('/login', location.href)
  }

  setupUnsavedHarvestingWarning(reduxStore, logger)

  return reduxStore
}
